import {byteToHexString, hexStringToByte, sleep} from "./util";
import logger from "./logger";

/* eslint-disable no-throw-literal */
export const TYPES = {
  SET_DEVICE: Symbol('SET_DEVICE'),
  APPEND_APDU_LOG: Symbol('APPEND_APDU_LOG'),
  SET_ADMIN_AUTHENTICATED: Symbol('SET_ADMIN_AUTHENTICATED'),
  SET_MODEL: Symbol('SET_MODEL'),
  SET_FIRMWARE_VERSION: Symbol('SET_FIRMWARE_VERSION'),
}

function setDevice(device) {
  return {
    type: TYPES.SET_DEVICE,
    device
  }
}

// Global connection lock to prevent concurrent connections
let connectionLock = Promise.resolve();

async function runWithConnectionLock(task) {
  connectionLock = connectionLock.then(task, task);
  return connectionLock;
}

export function connect() {
  return async (dispatch, getState) => {
    // Wrap the connection logic in the lock
    return runWithConnectionLock(async () => {
      const currentDevice = getState().device;
      if (currentDevice && currentDevice.opened) {
        return true;
      }

      // Check if WebUSB is available
      if (!navigator.usb) {
        throw new Error('WebUSB API is not available. Please use Chrome, Edge, or Opera browser.');
      }

      let device;
      try {
        device = await navigator.usb.requestDevice({
          filters: [{
            classCode: 0xFF, // vendor specific
          }]
        });
      } catch (err) {
        // Distinguish between user cancellation and actual errors
        if (err.name === 'NotFoundError') {
          console.debug('User cancelled device selection');
          return false;
        } else {
          console.error('Device selection failed:', err);
          throw new Error(`Device selection failed: ${err.message}`);
        }
      }

      if (device === undefined) {
        return false;
      }

      await device.open();
      await device.claimInterface(1);
      await transceive_webusb(device, "00A4040005F000000000");
      dispatch(setDevice(device));
      dispatch(setAdminAuthenticated(false));
      navigator.usb.addEventListener('disconnect', event => {
        console.log(`USB device disconnected ${event}`);
        if (event.device === device) {
          dispatch(setDevice(null));
          dispatch(setAdminAuthenticated(false));
        }
      });
      return true;
    });
  };
}

// Auto-reconnect to previously authorized device without showing picker
export function autoConnect() {
  return async (dispatch, getState) => {
    // Wrap the auto-connect logic in the lock
    return runWithConnectionLock(async () => {
      const currentDevice = getState().device;
      if (currentDevice && currentDevice.opened) {
        return true;
      }

      try {
        // Get list of previously authorized devices
        const devices = await navigator.usb.getDevices();

        // Find GriffonOakey device (vendor-specific class 0xFF)
        const device = devices.find(d => {
          // Check if any interface has vendor-specific class
          return d.configurations.some(config =>
            config.interfaces.some(iface =>
              iface.alternates.some(alt => alt.interfaceClass === 0xFF)
            )
          );
        });

        if (device) {
          if (!device.opened) {
            await device.open();
          }
          await device.claimInterface(1);
          await transceive_webusb(device, "00A4040005F000000000");
          dispatch(setDevice(device));
          dispatch(setAdminAuthenticated(false));
          navigator.usb.addEventListener('disconnect', event => {
            console.log(`USB device disconnected ${event}`);
            if (event.device === device) {
              dispatch(setDevice(null));
              dispatch(setAdminAuthenticated(false));
            }
          });
          return true;
        }
        return false;
      } catch (err) {
        console.log('Auto-connect failed:', err);
        return false;
      }
    });
  };
}

export function disconnect() {
  // 断开连接时清除认证缓存
  try {
    sessionStorage.removeItem('adminAuthenticated');
    sessionStorage.removeItem('adminAuthTimestamp');
    console.log('✓ Cleared admin auth cache on disconnect');
  } catch (e) {
    console.warn('Failed to clear auth cache:', e);
  }
  
  return {
    type: TYPES.SET_DEVICE,
    device: null
  }
}

export function setModel(model) {
  return {
    type: TYPES.SET_MODEL,
    model: model
  }
}

export function setFirmwareVersion(firmwareVersion) {
  return {
    type: TYPES.SET_FIRMWARE_VERSION,
    firmwareVersion: firmwareVersion
  }
}

export function setAdminAuthenticated(state) {
  return {
    type: TYPES.SET_ADMIN_AUTHENTICATED,
    adminAuthenticated: state,
  }
}

// Verify admin authentication by querying device status
// This is the single source of truth - always check with the actual device
export async function verifyAdminAuth(dispatch, device = null, silent = false) {
  if (!device) return false;
  try {
    // Try to select admin applet
    const res = await dispatch(transceive("00A4040005F000000000"));
    if (!res.endsWith("9000")) {
      dispatch(setAdminAuthenticated(false));
      return false;
    }
    
    // Try a read-only operation that requires authentication
    // Using HOTP test command (0x02) which will return:
    // - 9000 or 6A80 if authenticated
    // - 6982 if not authenticated
    const testRes = await dispatch(transceive("0002000000"));
    
    if (testRes.endsWith("9000") || testRes.endsWith("6A80")) {
      // Authenticated (6A80 = wrong data but command accepted)
      if (!silent) console.log('✓ Admin authenticated');
      dispatch(setAdminAuthenticated(true));
      return true;
    } else if (testRes.endsWith("6982")) {
      // Not authenticated (security status not satisfied)
      if (!silent) console.log('✗ Admin not authenticated');
      dispatch(setAdminAuthenticated(false));
      return false;
    }
    
    // Unknown response, assume not authenticated
    if (!silent) console.warn('Admin auth check: Unknown response');
    dispatch(setAdminAuthenticated(false));
    return false;
  } catch (e) {
    // On error, assume not authenticated
    if (!silent) console.debug('Admin auth check failed:', e);
    dispatch(setAdminAuthenticated(false));
    return false;
  }
}

export function appendAPDULog(capdu, rapdu) {
  return {
    type: TYPES.APPEND_APDU_LOG,
    capdu: capdu.toUpperCase(),
    rapdu: rapdu.toUpperCase()
  }
}

// 全局 APDU 队列，确保同一时间只有一个命令在处理
let apduQueue = Promise.resolve();

async function transceive_webusb(device, capdu) {
  // 将当前请求加入队列，确保串行执行
  const task = () => transceive_webusb_impl(device, capdu);
  
  // 无论前一个任务成功还是失败，都执行当前任务
  apduQueue = apduQueue.then(task, task);
  
  return apduQueue;
}

async function transceive_webusb_impl(device, capdu) {
  const startTime = performance.now();
  let data = hexStringToByte(capdu);

  // 等待设备就绪（只接受 STATE_IDLE）
  let retries = 100; // 最多等待 1 秒（100 * 10ms）
  let waitStartTime = performance.now();
  while (retries > 0) {
    try {
      const statusResp = await device.controlTransferIn({
        requestType: 'vendor',
        recipient: 'interface',
        request: 2, // WEBUSB_REQ_STAT
        value: 0,
        index: 1
      }, 1);

      if (statusResp.status === 'ok') {
        const status = new Uint8Array(statusResp.data.buffer)[0];

        // 状态值：-1=IDLE, 0=SENDING_RESP, 1=PROCESS, 2=SENT_RESP, 3=RECVING, 4=HOLD_BUF
        // 只有 STATE_IDLE (255/-1) 才是真正空闲，可以安全发送新命令
        // STATE_HOLD_BUF (4) 表示缓冲区还在保持中，需要等待释放
        if (status === 255) { // 255是-1的uint8表示，即STATE_IDLE
          break;
        } else if (status === 4) {
          // STATE_HOLD_BUF: 缓冲区保持中，等待设备释放（固件2秒超时）
          await sleep(50); // 50ms 足够，比之前的 100ms 快一倍
          retries--;
          continue;
        } else {
          // 其他状态（处理中、发送中等），等待
          await sleep(10); // 大部分情况下 10ms 就够了
          retries--;
          continue;
        }
      } else {
        // statusResp.status 不是 'ok'，等待重试
        await sleep(10);
        retries--;
        continue;
      }
    } catch (err) {
      // Status check failed, continue trying
      console.debug('Status check failed, retrying...', err);
      await sleep(10);
      retries--;
      continue;
    }
  }

  if (retries === 0) {
    throw new Error('WebUSB设备状态检查超时 - 设备可能繁忙或未就绪');
  }

  const waitIdleTime = performance.now() - waitStartTime;
  if (waitIdleTime > 100) {
    console.debug(`⏱️ Wait for IDLE: ${waitIdleTime.toFixed(1)}ms`);
  }

  // send a command
  let resp = await device.controlTransferOut({
    requestType: 'vendor',
    recipient: 'interface',
    request: 0, // WEBUSB_REQ_CMD
    value: 0,
    index: 1
  }, data);

  // wait for execution - 等待设备处理命令
  // 状态会从 STATE_RECVING(3) -> STATE_PROCESS(1) -> STATE_SENDING_RESP(0)
  let maxRetries = 50; // 最多等待5秒
  while (maxRetries > 0) {
    await sleep(100);

    resp = await device.controlTransferIn({
      requestType: 'vendor',
      recipient: 'interface',
      request: 2, // WEBUSB_REQ_STAT
      value: 0,
      index: 1
    }, 1);

    if (resp.status === 'stalled') {
      throw new Error('Device stalled - 设备繁忙');
    }

    let code = new Uint8Array(resp.data.buffer)[0];

    // code=0 表示 STATE_SENDING_RESP，说明响应已准备好
    if (code === 0) {
      break;
    }
    // code=1 表示 STATE_PROCESS，继续等待
    else if (code === 1) {
      maxRetries--;
      continue;
    }
    // code=3 表示 STATE_RECVING，继续等待
    else if (code === 3) {
      maxRetries--;
      continue;
    }
    // 其他状态异常
    else {
      console.warn(`Unexpected state ${code} while waiting for response`);
      maxRetries--;
    }
  }

  if (maxRetries === 0) {
    throw new Error('等待设备响应超时');
  }

  // get the response
  resp = await device.controlTransferIn({
    requestType: 'vendor',
    recipient: 'interface',
    request: 1, // WEBUSB_REQ_RESP
    value: 0,
    index: 1
  }, 1500);

  if (resp.status === "ok") {
    const responseData = byteToHexString(new Uint8Array(resp.data.buffer));
    const totalTime = performance.now() - startTime;
    
    // 🔒 安全增强：使用安全日志，避免泄露 APDU 详情
    logger.perf(`APDU ${capdu.substring(0, 4)}...`, totalTime, 500);
    
    return responseData;
  } else {
    throw new Error(`Response read failed: ${resp.status}`);
  }
}

export function transceive(capdu, is_secret, in_oath) {
  return async (dispatch, getState) => {
    const {device} = getState();
    let rapdu = '';
    do {
      let remain = rapdu.slice(-2);
      if(remain !== '') { // get response
        if (in_oath) {
          capdu = `00060000${remain}`;
        } else {
          capdu = `00C00000${remain}`;
        }
        rapdu = rapdu.slice(0, -4);
      }
      rapdu += await transceive_webusb(device, capdu);
      if (is_secret) {
        dispatch(appendAPDULog('REDACTED', rapdu));
      } else {
        dispatch(appendAPDULog(capdu, rapdu));
      }
    } while(rapdu.slice(-4, -2) === "61");
    return rapdu;
  };
}
